import {findFilterByName} from "../context/helpers";
import { DataTableSortStatus } from "mantine-datatable";
import {EvidenceInclusion, GlobalFilters} from "../components/ideas/store/types";
import { resolveDynamicDateKey } from "./dynamic-date-utils";

export interface IdeasUrlState {
  page: string;
  selectedView: string | null;
  sortStatus: DataTableSortStatus | null;
  globalFilters: GlobalFilters;
  tabFilters: any[];
  evidenceInclusion: EvidenceInclusion;
}

export const parseIdeasUrlState = (
  queryString: string,
  ideasFiltersConfig?: { evidence: any; idea: any; }
): IdeasUrlState => {
  const searchParams = new URLSearchParams(queryString);
  
  const page = searchParams.get("page") || "1";
  const selectedView = searchParams.get("selectedView");
  
  // SortStatus
  let sortStatus: DataTableSortStatus | null = null;
  const sortStatusStr = searchParams.get("sortStatus");
  if (sortStatusStr) {
    try {
      const parsed = JSON.parse(sortStatusStr);
      if (parsed?.columnAccessor && parsed.columnAccessor !== "null") {
        sortStatus = parsed;
      }
    } catch (e) {
      console.error("Invalid sortStatus in URL:", e);
    }
  }
  
  // Global Filters
  const globalFilters: IdeasUrlState['globalFilters'] = {
    owner: undefined,
    productArea: undefined,
    dateRange: null
  };
  
  const ownerParam = searchParams.get("global.owner");
  if (ownerParam) {
    globalFilters.owner = ownerParam.split(',').filter(Boolean);
  }
  
  const productAreaParam = searchParams.get("global.productArea");
  if (productAreaParam) {
    globalFilters.productArea = productAreaParam.split(',').filter(Boolean);
  }
  
  const dateRangeParam = searchParams.get("global.dateRange");
  if (dateRangeParam) {
    try {
      globalFilters.dateRange = JSON.parse(dateRangeParam);
    } catch (e) {
      console.error("Invalid dateRange in URL:", e);
    }
  }
  
  // Evidence Inclusion
  const evidenceInclusionParam = searchParams.get("evidenceInclusion");
  const evidenceInclusion: EvidenceInclusion = 
    (evidenceInclusionParam === 'assigned' || evidenceInclusionParam === 'suggested' || evidenceInclusionParam === 'total')
      ? evidenceInclusionParam
      : 'total';
  
  // Tab Filters (filter.evidence.* та filter.idea.*)
  const tabFilters: any[] = [];
  
  searchParams.forEach((value, key) => {
    if (key.startsWith('filter.')) {
      // Remove the 'filter.' prefix and split the key into parts.
      const parts = key.replace('filter.', '').split('.');
      if (parts.length < 2) return;

      // The first part is the filterObject ("evidence" or "idea"), the rest is the filter name.
      const filterObject = parts[0] as "evidence" | "idea";
      const name = parts.slice(1).join('.');

      // Parse the stored value.
      let parsedValue: any;
      try {
        parsedValue = JSON.parse(value);
      } catch (e) {
        parsedValue = value;
      }

      // Start with a basic filter object.
      let filterData: any = {
        filterObject,
        name
      };

      // Retrieve filter configuration if provided.
      const filterConfig = ideasFiltersConfig
        ? findFilterByName(name, filterObject, ideasFiltersConfig)
        : null;

      // Use filterConfig to decide how to interpret the parsed value.
      if (filterConfig && (filterConfig.type === 'number' || filterConfig.type === 'boolean')) {
        // For number/boolean filters, we expect an object with an operator.
        if (
          typeof parsedValue === 'object' &&
          parsedValue !== null &&
          'operator' in parsedValue
        ) {
          filterData.operator = parsedValue.operator;
          filterData.query = parsedValue.value;
        } else {
          // Fallback: assign the parsed value to selected.
          filterData.selected = parsedValue;
        }
      } else {
        // For other filter types, simply assign the parsed value to selected.
        filterData.selected = parsedValue;
      }

      // Merge in the filter configuration if available.
      if (filterConfig) {
        filterData = { ...filterConfig, ...filterData };
      }

      tabFilters.push(filterData);
    }
  });

  return {
    page,
    selectedView,
    sortStatus,
    globalFilters,
    tabFilters,
    evidenceInclusion
  };
};


export const serializeIdeasUrlState = (state: Partial<IdeasUrlState>): string => {
  const searchParams = new URLSearchParams();
  
  if (state.page) {
    searchParams.set("page", state.page);
  }
  
  if (state.selectedView) {
    // If selectedView is an object, extract the name; otherwise use it as string
    const viewName = typeof state.selectedView === 'string' 
      ? state.selectedView 
      : (state.selectedView?.name || state.selectedView);
    if (viewName) {
      searchParams.set("selectedView", String(viewName));
    }
  }
  
  if (state.sortStatus) {
    searchParams.set("sortStatus", JSON.stringify(state.sortStatus));
  }
  
  // Global Filters
  if (state.globalFilters) {
    if (state.globalFilters.owner && state.globalFilters.owner.length > 0) {
      searchParams.set("global.owner", state.globalFilters.owner.join(','));
    }
    
    if (state.globalFilters.productArea && state.globalFilters.productArea.length > 0) {
      searchParams.set("global.productArea", state.globalFilters.productArea.join(','));
    }
    
    if (state.globalFilters.dateRange) {
      searchParams.set("global.dateRange", JSON.stringify(state.globalFilters.dateRange));
    }
  }
  
  // Evidence Inclusion
  if (state.evidenceInclusion) {
    searchParams.set("evidenceInclusion", state.evidenceInclusion);
  }
  
  // Tab Filters
  if (state.tabFilters) {
    state.tabFilters.forEach(filter => {
      if (!filter.filterObject || !filter.name) return;
      
      const key = `filter.${filter.filterObject}.${filter.name}`;
      
      let value: any = null;
      // For number and boolean types, include operator with the active value.
      if (filter.type === 'number' || filter.type === 'boolean') {
        if (filter.operator && (filter.query !== null && filter.query !== undefined)) {
          value = { operator: filter.operator, value: filter.query };
        } else if (filter.operator && filter.selected !== undefined) {
          value = { operator: filter.operator, value: filter.selected };
        }
      } else if (filter.type === 'date') {
        if (Array.isArray(filter.selected) && filter.selected.length === 2) {
          value = filter.selected;
        } else {
          const dynamicKey = filter.savedKey || filter.operator;
          if (dynamicKey && typeof dynamicKey === 'string') {
            const resolved = resolveDynamicDateKey(dynamicKey);
            if (resolved) {
              value = [resolved[0].toISOString(), resolved[1].toISOString()];
            }
          }
        }
      } else {
        // For other filter types, use query if available; otherwise, use selected.
        if (filter.query !== null && filter.query !== undefined) {
          value = filter.query;
        } else if (filter.selected !== undefined &&
                   !(Array.isArray(filter.selected) && filter.selected.length === 0)) {
          value = filter.selected;
        }
      }
      
      if (value !== null) {
        searchParams.set(key, typeof value === 'string' ? value : JSON.stringify(value));
      }
    });
  }
  
  return searchParams.toString();
};

export const mergeFilterLayers = (
  globalFilters: any[] = [],
  pageFilters: any[] = [],
  viewFilters: any[] = [],
  userFilters: any[] = []
): any[] => {
  // Defensive: ensure all inputs are arrays to prevent "is not iterable" errors
  const safeGlobalFilters = Array.isArray(globalFilters) ? globalFilters : [];
  const safePageFilters = Array.isArray(pageFilters) ? pageFilters : [];
  const safeViewFilters = Array.isArray(viewFilters) ? viewFilters : [];
  const safeUserFilters = Array.isArray(userFilters) ? userFilters : [];
  
  const allFilters = [
    ...safeGlobalFilters,
    ...safePageFilters,
    ...safeViewFilters,
    ...safeUserFilters
  ];
  
  const uniqueFilters: any[] = [];
  const seenNames = new Set<string>();
  
  for (let i = allFilters.length - 1; i >= 0; i--) {
    const filter = allFilters[i];
    const name = (filter?.name || filter?.parent || '').toLowerCase();
    
    if (name && !seenNames.has(name)) {
      seenNames.add(name);
      uniqueFilters.unshift(filter);
    }
  }
  
  return uniqueFilters;
};

// Helper function to compare sortStatus objects
export const compareSortStatus = (a: DataTableSortStatus | null, b: DataTableSortStatus | null): boolean => {
  if (!a && !b) return true;
  if (!a || !b) return false;
  return a.columnAccessor === b.columnAccessor && a.direction === b.direction;
};

/**
 * Builds an idea page URL with evidence filters preserved from the current URL.
 * Used for navigating to idea details while maintaining evidence filter context.
 * Also preserves global.dateRange as a filter.evidence.createdAt for the idea page.
 * When activeFilters is provided, view-level dynamic evidence filters are also included.
 */
export const buildIdeaUrlWithEvidenceFilters = (
  ideaId: string,
  currentSearch?: string,
  activeFilters?: any[],
  options?: { selectedTheme?: string }
): string => {
  const searchString = currentSearch ?? window.location.search;
  const currentParams = new URLSearchParams(searchString);
  const evidenceFilters = new URLSearchParams();

  console.log('🔍 [buildIdeaUrlWithEvidenceFilters] Input:', {
    ideaId,
    currentSearch,
    searchString,
    allParams: Object.fromEntries(currentParams.entries())
  });

  currentParams.forEach((value, key) => {
    // Include evidence filters from tabFilters
    if (key.startsWith("filter.evidence")) {
      evidenceFilters.set(key, value);
    }
    // Also include global.dateRange and convert it to filter.evidence.CreatedAt format
    if (key === "global.dateRange" && value) {
      try {
        const dateRange = JSON.parse(value);
        if (dateRange && dateRange.start && dateRange.end) {
          // Convert to the format expected by parseFiltersFromQuery
          // Use 'CreatedAt' (capital C and A) to match the expected filter name
          const filterValue = JSON.stringify([dateRange.start, dateRange.end]);
          evidenceFilters.set("filter.evidence.CreatedAt", filterValue);
          
          console.log('🔍 [buildIdeaUrlWithEvidenceFilters] Converting global.dateRange to filter.evidence.CreatedAt:', {
            original: value,
            converted: filterValue
          });
        }
      } catch (e) {
        console.warn('Failed to parse global.dateRange:', e);
      }
    }
  });

  // Serialize view-level dynamic evidence filters using the existing serializeIdeasUrlState logic.
  // These are not present in the URL params so must be added explicitly.
  if (Array.isArray(activeFilters)) {
    const evidenceOnlyFilters = activeFilters.filter(
      (filter: any) => filter.filterObject === 'evidence' && filter.name
    );
    const serializedParams = new URLSearchParams(
      serializeIdeasUrlState({ tabFilters: evidenceOnlyFilters })
    );
    serializedParams.forEach((value, key) => {
      // User-applied tab filters already in the URL take precedence
      if (!evidenceFilters.has(key)) {
        evidenceFilters.set(key, value);
      }
    });
  }

  if (options?.selectedTheme) {
    evidenceFilters.set("selectedTheme", options.selectedTheme);
  }

  const queryString = evidenceFilters.toString();
  return `/idea/${ideaId}${queryString ? `?${queryString}` : ""}`;
};